"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Mail, ArrowRight, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function FinalCta() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Bracknell",
    to: "/AreasWeCoverPage"
  }, {
    name: "Reading",
    to: "/AreasWeCoverPage"
  }, {
    name: "Wokingham",
    to: "/AreasWeCoverPage"
  }, {
    name: "Ascot",
    to: "/AreasWeCoverPage"
  }, {
    name: "Windsor",
    to: "/AreasWeCoverPage"
  }, {
    name: "Maidenhead",
    to: "/AreasWeCoverPage"
  }, {
    name: "Camberley",
    to: "/AreasWeCoverPage"
  }];
  return <section id="finalcta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}>
          <Card className="border border-border bg-card shadow-lg overflow-hidden">
            <CardContent className="p-8 sm:p-12 lg:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                {/* Left Column: Headline and Direct Contacts */}
                <div className="lg:col-span-7 space-y-6">
                  <div className="space-y-3">
                    <span className="text-xs font-semibold tracking-wider text-primary uppercase">
                      Direct contact &amp; estimates
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-card-foreground font-default">
                      Request a scaffolding quote
                    </h2>
                    <p className="text-base sm:text-lg text-muted-foreground font-default max-w-xl">
                      Tell us about your project or speak directly with Pete and
                      the team for practical advice and clear site specifications.
                    </p>
                  </div>

                  {/* Contact channels */}
                  <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <a href="tel:01344929900" className="group flex items-center gap-3.5 p-4 rounded-lg border border-border bg-background text-foreground hover:bg-accent hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring transition-[color,background-color,border-color] duration-200">
                      <div className="flex size-10 items-center justify-center rounded-md bg-secondary text-secondary-foreground shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-[color,background-color] duration-200">
                        <Phone className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground group-hover:text-foreground/80">
                          Call our office
                        </p>
                        <p className="text-base font-semibold text-foreground group-hover:text-primary transition-[color] duration-200 truncate">
                          01344 929900
                        </p>
                      </div>
                    </a>

                    <a href="mailto:quotes@bmfscaffolding.co.uk" className="group flex items-center gap-3.5 p-4 rounded-lg border border-border bg-background text-foreground hover:bg-accent hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring transition-[color,background-color,border-color] duration-200">
                      <div className="flex size-10 items-center justify-center rounded-md bg-secondary text-secondary-foreground shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-[color,background-color] duration-200">
                        <Mail className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground group-hover:text-foreground/80">
                          Email specifications
                        </p>
                        <p className="text-sm sm:text-base font-semibold text-foreground truncate">
                          quotes@bmfscaffolding.co.uk
                        </p>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Right Column: Main CTA Action Box */}
                <div className="lg:col-span-5 bg-secondary text-secondary-foreground p-8 sm:p-10 rounded-xl flex flex-col justify-between space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-xl font-bold font-default text-secondary-foreground">
                      Ready to start your quote?
                    </h3>
                    <p className="text-sm text-secondary-foreground/80 leading-relaxed font-default">
                      Submit your site location, scaffold requirements, and schedule.
                      We provide responsive, transparent quotes for commercial,
                      residential, and solar projects.
                    </p>
                  </div>

                  <div className="pt-2">
                    <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm h-12">
                      <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                        <span>Request a scaffolding quote</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                    <p className="mt-3 text-center text-xs text-secondary-foreground/70">
                      NASC &amp; CHAS accredited
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Row: Nearby Areas */}
              <div className="mt-12 pt-8 border-t border-border">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
                    <MapPin className="size-3.5 text-primary" />
                    <span>Nearby areas</span>
                  </div>

                  <nav aria-label="Nearby areas list" className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                    {nearbyAreas.map((area, i) => <React.Fragment key={area.name}>
                        <Link to={area.to} data-index={i} className="text-muted-foreground hover:text-primary transition-[color] font-medium">
                          {area.name}
                        </Link>
                        {i < nearbyAreas.length - 1 && <span className="text-border" aria-hidden="true">
                            •
                          </span>}
                      </React.Fragment>)}
                    <Link to="/AreasWeCoverPage" className="text-primary hover:underline font-semibold ml-auto text-xs">
                      View all coverage areas
                    </Link>
                  </nav>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}